'use client';

import { useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Button } from '@ghq-abi/design-system-v2';
import { useTranslate } from '@tolgee/react';

import { useUser } from '~/shared/auth/useUser';

import { Icon } from '../Icon';

const ALL_REQUESTS_PATH = '/all-requests';

const BANNER_Z = 1201;

export function AppBanner() {
  const { t } = useTranslate();
  const router = useRouter();
  const { status } = useSession();
  const user = useUser();
  const bannerRef = useRef<HTMLDivElement>(null);

  const canTakeAction = Boolean(user?.isApprover);

  const onTakeAction = useCallback(() => {
    if (!canTakeAction) return;
    router.push(ALL_REQUESTS_PATH);
  }, [canTakeAction, router]);

  useLayoutEffect(() => {
    if (status !== 'authenticated') {
      document.documentElement.style.setProperty('--app-banner-height', '0px');
      return undefined;
    }

    const el = bannerRef.current;
    if (!el) {
      document.documentElement.style.setProperty('--app-banner-height', '0px');
      return undefined;
    }

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--app-banner-height',
        `${el.offsetHeight}px`
      );
    };

    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);

    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty('--app-banner-height', '0px');
    };
  }, [status]);

  if (status !== 'authenticated') {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className="flex justify-between items-center fixed left-0 right-0 top-0 w-full h-auto bg-[#F2DC39] py-1 px-6 font-medium text-[#191F2E]"
      data-testid="app-banner"
      role="banner"
      style={{ zIndex: BANNER_Z }}
    >
      {t('common.app_banner.take_action')}
      <Button
        type="button"
        disabled={!canTakeAction}
        onClick={onTakeAction}
        iconRight={<Icon icon="diagonalArrow" size={14} isCustom />}
      >
        {t('common.app_banner.take_action.button')}
      </Button>
    </div>
  );
}
